import { api } from '../api.ts';
import { fromBase64, toBase64 } from '../crypto/primitives.ts';
import { wrapDekFor } from '../crypto/keypair.ts';
import { clear, el, hinweis, karte } from '../ui.ts';
import {
  alleSafes,
  gastEinladen,
  meinSafe,
  mitgliedAufnehmen,
  mitgliederVon,
  personen,
  safeSchluessel,
  schluesselVerteilen,
  vorname,
  type Mitglied,
  type Person,
  type SafeAnzeige,
} from '../safes.ts';
import {
  fachAnlegen,
  fachAufloesen,
  fachPersonenAendern,
  kreiseAktualisieren,
  type GeoeffneterKreis,
} from '../schluesselkreise.ts';
import { tuerschild } from './safes-ui.ts';
import {
  fehlertext,
  metadatenAuffrischen,
  ordnerDesSafes,
  ordnerMitUnterordnern,
  safeIdVon,
  sorgeFuerEntschluesselte,
  state,
} from './gemeinsam.ts';

/**
 * Schlüssel verwalten (Familien-Safes 16.10.2026, Vereine/Firmen und Gastkonten
 * 17.10.2026). Überall dieselbe Bedienung: eine Liste von Menschen, je ein großer
 * Schalter, jede Änderung spricht einen Satz.
 *
 *  - „Wer darf in meinen Safe schauen?" – Gäste für den eigenen Safe: Leute aus
 *    gemeinsamen Safes, oder per E-Mail eingeladene (auch ohne Konto).
 *  - Je gemeinsamem Safe: Wer hat den Schlüssel, Jemanden aufnehmen (Sitzprüfung in
 *    der Verwaltung), Fächer nur für bestimmte Leute.
 *
 * Kryptografisch: Der Besitzer verpackt im Browser Dokumentschlüssel für den
 * öffentlichen Personenschlüssel des Gastes (nie den Safe-Schlüssel); Fächer sind
 * Schlüsselkreise am Ordner. Zurückziehen löscht Umschläge.
 */

const PERSONEN_FARBEN = ['#7a4ea3', '#2f6fb3', '#a3503f', '#4f7a2a', '#8a6d1f', '#1f4e79'];

export function schluesselKarte(): HTMLElement {
  const inhalt = el('div', { id: 'safe-schluessel' }, el('p', { class: 'loading' }, 'Wird geladen …'));
  void ladeSchluessel(inhalt);
  const gast = state.me?.gast === true;
  return karte(
    el('h2', {}, gast ? 'Safes, die für mich geöffnet sind' : 'Wer darf in meinen Safe schauen?'),
    gast
      ? null
      : el(
          'p',
          { class: 'leise' },
          'Menschen aus Ihrer Familie, Ihrem Verein oder Ihrer Firma – oder jemand, den Sie per E-Mail ' +
            'einladen – können einen Schlüssel zu Ihrem Safe bekommen: für alles oder nur für bestimmte ' +
            'Fächer. Löschen kann in Ihrem Safe nur, wer ihn besitzt: Sie.',
        ),
    inhalt,
  );
}

async function ladeSchluessel(inhalt: HTMLElement): Promise<void> {
  try {
    const mein = meinSafe();
    const { personen: liste } = await personen();
    clear(inhalt);

    if (mein) {
      if (liste.length === 0) {
        inhalt.append(el('p', { class: 'leise' }, 'Noch hat niemand einen Schlüssel zu Ihrem Safe.'));
      }
      liste.forEach((person, i) => inhalt.append(personZeile(person, mein, i, inhalt)));
      inhalt.append(einladenZeile(mein, inhalt));
    } else if (!state.me?.gast) {
      inhalt.append(hinweis('Für dieses Konto gibt es noch keinen eigenen Safe im Familienmodell. Rufen Sie uns an, wir richten ihn ein.'));
    }

    const gast = alleSafes().filter((s) => s.zugang === 'gast');
    if (gast.length > 0) {
      if (mein) inhalt.append(el('h3', {}, 'Für mich geöffnet'));
      inhalt.append(
        el(
          'ul',
          { class: 'stand-liste' },
          ...gast.map((s) =>
            el(
              'li',
              {},
              tuerschild(s),
              el('span', {}, el('b', {}, s.titel), ` – ${s.umfang === 'faecher' ? 'nur bestimmte Fächer' : 'alles'}${s.darfAblegen ? ', ich darf auch ablegen' : ''}`),
            ),
          ),
        ),
      );
    } else if (state.me?.gast) {
      inhalt.append(el('p', { class: 'leise' }, 'Der Schlüssel kommt automatisch, sobald sich der Besitzer das nächste Mal anmeldet.'));
    }
  } catch (error) {
    clear(inhalt);
    inhalt.append(hinweis(fehlertext(error), 'warnung'));
  }
}

// --- Gäste des eigenen Safes ---------------------------------------------------

function beschreibung(person: Person): string {
  if (!person.bereit) {
    return person.eingeladen
      ? 'eingeladen – wartet auf die erste Anmeldung, dann kommt der Schlüssel automatisch'
      : 'hat sich noch nie angemeldet – vorher ist kein Schlüssel möglich';
  }
  if (!person.gast) return person.gemeinsam ? `aus „${person.gemeinsam}" · kein Schlüssel` : 'kein Schlüssel';
  const umfang = person.gast.umfang === 'alles' ? 'darf alles sehen' : `darf ${person.gast.faecher.length} ${person.gast.faecher.length === 1 ? 'Fach' : 'Fächer'} sehen`;
  return `${umfang}${person.gast.darfAblegen ? ' und ablegen' : ''}${person.gast.ausstehend ? ' · Schlüssel wird gerade übergeben' : ''}`;
}

function personZeile(person: Person, mein: SafeAnzeige, index: number, liste: HTMLElement): HTMLElement {
  const farbe = PERSONEN_FARBEN[index % PERSONEN_FARBEN.length]!;
  const status = el('div', {});
  const optionen = el('div', { class: 'person-optionen' });
  const id = `gast-${person.personId}`;

  const schalter = el('input', {
    type: 'checkbox',
    role: 'switch',
    class: 'schalter',
    id,
    'aria-label': `${person.name} darf in meinen Safe schauen`,
    ...(person.gast ? { checked: true } : {}),
    ...(person.bereit || person.gast ? {} : { disabled: true }),
  }) as HTMLInputElement;
  schalter.addEventListener('change', () => {
    if (schalter.checked) void setzen(person, mein, 'alles', false, [], status, liste);
    else void entziehen(person, mein, status, liste);
  });

  if (person.gast && person.bereit) {
    const gast = person.gast;
    const umfangName = `umfang-${person.personId}`;
    const faecherPlatz = el('div', {});
    const zeigeFaecher = (): void => {
      clear(faecherPlatz);
      const oben = ordnerDesSafes(mein.kundeId).filter((o) => !o.parent_id).sort((a, b) => a.name.localeCompare(b.name, 'de'));
      const gewaehlt = new Set(gast.faecher);
      faecherPlatz.append(
        el('p', { class: 'leise', style: 'margin:.3rem 0' }, 'Welche Fächer (Ordner samt Unterordnern)?'),
        oben.length
          ? el(
              'div',
              { class: 'faecher-liste' },
              ...oben.map((o) =>
                el('label', {}, el('input', {
                  type: 'checkbox',
                  value: o.id,
                  ...(gewaehlt.has(o.id) ? { checked: true } : {}),
                  onchange: (e: Event) => {
                    const box = e.target as HTMLInputElement;
                    if (box.checked) gewaehlt.add(o.id);
                    else gewaehlt.delete(o.id);
                    if (gewaehlt.size === 0) {
                      clear(status);
                      status.append(hinweis('Bitte mindestens ein Fach wählen – oder oben „alles" wählen.', 'warnung'));
                      return;
                    }
                    void setzen(person, mein, 'faecher', gast.darfAblegen, [...gewaehlt], status, liste);
                  },
                }), `📁 ${o.name}`),
              ),
            )
          : el('p', { class: 'leise' }, 'Ihr Safe hat noch keine Ordner.'),
      );
    };
    const chip = (wert: 'alles' | 'faecher', text: string): HTMLElement =>
      el('label', {}, el('input', {
        type: 'radio',
        name: umfangName,
        value: wert,
        ...(gast.umfang === wert ? { checked: true } : {}),
        onchange: () => {
          if (wert === 'alles') void setzen(person, mein, 'alles', gast.darfAblegen, [], status, liste);
          else zeigeFaecher();
        },
      }), text);
    if (gast.umfang === 'faecher') zeigeFaecher();
    optionen.append(
      el('div', { class: 'wahl-chips' }, chip('alles', 'alles'), chip('faecher', 'nur bestimmte Fächer')),
      faecherPlatz,
      el('div', { class: 'wahl-chips' }, el('label', {}, el('input', {
        type: 'checkbox',
        ...(gast.darfAblegen ? { checked: true } : {}),
        onchange: (e: Event) => void setzen(person, mein, gast.umfang, (e.target as HTMLInputElement).checked, gast.faecher, status, liste),
      }), 'darf auch ablegen')),
    );
  }

  return el(
    'div',
    { class: 'person-zeile', style: `--k:${farbe}` },
    el(
      'div',
      { class: 'person-kopf' },
      el('span', { class: 'avatar', 'aria-hidden': 'true' }, vorname(person.name).charAt(0).toUpperCase()),
      el('label', { class: 'person-name', for: id }, person.name, el('small', {}, beschreibung(person))),
      schalter,
    ),
    optionen,
    status,
  );
}

/** Jemand anderem einen Schlüssel geben – per E-Mail, auch ohne Konto. */
function einladenZeile(mein: SafeAnzeige, liste: HTMLElement): HTMLElement {
  const status = el('div', {});
  const email = el('input', { type: 'email', id: 'gast-email', autocomplete: 'off', placeholder: 'name@beispiel.de' }) as HTMLInputElement;
  const name = el('input', { type: 'text', id: 'gast-name', autocomplete: 'off', placeholder: 'z. B. Oma Hilde' }) as HTMLInputElement;
  const knopf = el('button', { type: 'button' }, 'Schlüssel geben');
  knopf.addEventListener('click', () => void einladen());

  async function einladen(): Promise<void> {
    const adresse = email.value.trim();
    if (!adresse.includes('@')) {
      clear(status);
      status.append(hinweis('Bitte eine E-Mail-Adresse eingeben.', 'warnung'));
      return;
    }
    knopf.disabled = true;
    clear(status);
    status.append(el('p', { class: 'loading' }, 'Einladung wird verschickt …'));
    try {
      const r = await gastEinladen(mein.kundeId, { email: adresse, name: name.value.trim() || undefined, umfang: 'alles', darfAblegen: false, folderIds: [] });
      if (r.bereit && r.publicKey && r.algorithm) {
        // Hat schon ein Konto mit Schlüssel: Umschläge gleich mitgeben.
        const person: Person = { personId: r.personId, name: r.name, email: r.email, gemeinsam: null, bereit: true, algorithm: r.algorithm, publicKey: r.publicKey, gast: null };
        await setzen(person, mein, 'alles', false, [], status, liste);
        return;
      }
      await neuZeichnen(liste);
      liste.append(
        el(
          'div',
          { class: 'satz' },
          `✓ Einladung an ${adresse} ist raus. Sobald ${r.name} einmal ein Passwort festgelegt hat und Sie sich danach wieder anmelden, liegt der Schlüssel bereit.`,
        ),
      );
    } catch (error) {
      clear(status);
      status.append(hinweis(fehlertext(error), 'warnung'));
      knopf.disabled = false;
    }
  }

  return el(
    'div',
    {},
    el('h3', {}, 'Jemand anderem einen Schlüssel geben'),
    el('p', { class: 'leise' }, 'Auch jemandem, der den Akten-Safe noch nicht benutzt – er bekommt eine Einladung und legt nur ein Passwort fest. Einen eigenen Safe gibt es dabei nicht.'),
    el(
      'div',
      { class: 'einladen-zeile' },
      el('div', {}, el('label', { for: 'gast-email' }, 'E-Mail-Adresse'), email),
      el('div', {}, el('label', { for: 'gast-name' }, 'Name (freiwillig)'), name),
      knopf,
    ),
    status,
  );
}

/** Schlüssel geben oder ändern – verpackt die Dokumentschlüssel des eigenen Safes für den Gast. */
async function setzen(
  person: Person,
  mein: SafeAnzeige,
  umfang: 'alles' | 'faecher',
  darfAblegen: boolean,
  faecher: string[],
  status: HTMLElement,
  liste: HTMLElement,
): Promise<void> {
  if (!person.publicKey || !person.algorithm) return;
  clear(status);
  status.append(el('p', { class: 'loading' }, 'Schlüssel wird vorbereitet …'));
  try {
    await sorgeFuerEntschluesselte();
    let dokumente = state.opened.filter((d) => safeIdVon(d.meta.safe_kunde_id) === mein.kundeId);
    if (umfang === 'faecher') {
      const menge = ordnerMitUnterordnern(faecher);
      dokumente = dokumente.filter((d) => d.meta.folder_id && menge.has(d.meta.folder_id));
    }
    const pub = fromBase64(person.publicKey);
    const wraps = [];
    for (const [i, d] of dokumente.entries()) {
      if (i % 20 === 0) {
        clear(status);
        status.append(el('p', { class: 'loading' }, `Schlüssel wird vorbereitet … ${i} von ${dokumente.length}`));
      }
      wraps.push({ documentId: d.id, wrappedDek: toBase64(await wrapDekFor(d.dek, pub, person.algorithm)) });
    }
    await api.post(`/api/safes/${mein.kundeId}/gaeste`, { personId: person.personId, umfang, darfAblegen, folderIds: faecher, wraps });

    const name = vorname(person.name);
    const was = umfang === 'alles' ? 'alle Ihre Dokumente' : `die Dokumente in ${faecher.length} ${faecher.length === 1 ? 'Fach' : 'Fächern'}`;
    await neuZeichnen(liste);
    const satz = el(
      'div',
      { class: 'satz' },
      `✓ ${name} kann jetzt ${was} sehen${darfAblegen ? ' und neue ablegen' : ''}. Das steht in Ihrem Protokoll.`,
      el('button', { type: 'button', class: 'sekundaer klein', onclick: () => void entziehen(person, mein, satz, liste) }, 'Rückgängig'),
    );
    (liste.querySelector(`#gast-${person.personId}`)?.closest('.person-zeile') ?? liste).append(satz);
  } catch (error) {
    clear(status);
    status.append(hinweis(fehlertext(error), 'warnung'));
  }
}

async function entziehen(person: Person, mein: SafeAnzeige, status: HTMLElement, liste: HTMLElement): Promise<void> {
  clear(status);
  status.append(el('p', { class: 'loading' }, 'Schlüssel wird zurückgezogen …'));
  try {
    await api.delete(`/api/safes/${mein.kundeId}/gaeste/${person.personId}`);
    await neuZeichnen(liste);
    liste.append(
      el('div', { class: 'satz' }, `${vorname(person.name)} hat keinen Schlüssel mehr zu Ihrem Safe. Was bisher gelesen wurde, bleibt gelesen – ab jetzt ist nichts mehr zugänglich.`),
    );
  } catch (error) {
    clear(status);
    status.append(hinweis(fehlertext(error), 'warnung'));
  }
}

async function neuZeichnen(liste: HTMLElement): Promise<void> {
  await metadatenAuffrischen().catch(() => undefined);
  await ladeSchluessel(liste);
}

// --- Gemeinsame Safes: Familie, Verein, Firma -------------------------------------

/** Eine Karte je gemeinsamem Safe: Mitglieder, Aufnehmen, Fächer. */
export function gemeinsameSafesKarten(): HTMLElement[] {
  return alleSafes()
    .filter((s) => s.zugang === 'mitglied' && s.typ !== 'privat')
    .map((safe) => {
      const inhalt = el('div', {}, el('p', { class: 'loading' }, 'Wird geladen …'));
      void ladeGemeinsamenSafe(safe, inhalt);
      return karte(el('h2', {}, tuerschild(safe), ' ', safe.titel), inhalt);
    });
}

async function ladeGemeinsamenSafe(safe: SafeAnzeige, inhalt: HTMLElement): Promise<void> {
  try {
    const mitglieder = await mitgliederVon(safe.kundeId);
    const istInhaber = safe.rolle === 'inhaber';
    clear(inhalt);
    inhalt.append(mitgliederStand(safe, mitglieder, inhalt));
    if (istInhaber) {
      inhalt.append(aufnehmenZeile(safe, inhalt));
      if (!safe.gesperrt) inhalt.append(await faecherAbschnitt(safe, mitglieder, inhalt));
    }
  } catch (error) {
    clear(inhalt);
    inhalt.append(hinweis(fehlertext(error), 'warnung'));
  }
}

function mitgliederStand(safe: SafeAnzeige, mitglieder: Mitglied[], inhalt: HTMLElement): HTMLElement {
  const offen = mitglieder.filter((m) => !m.ich && m.bereit && !m.hatSchluessel);
  const status = el('div', {});
  return el(
    'div',
    {},
    el('h3', {}, 'Wer hat einen Schlüssel?'),
    safe.gesperrt
      ? hinweis('Ihr Schlüssel zu diesem Safe kommt automatisch, sobald sich jemand, der ihn schon hat, das nächste Mal anmeldet.')
      : null,
    el(
      'ul',
      { class: 'stand-liste' },
      ...mitglieder.map((m) =>
        el(
          'li',
          {},
          el('span', { class: m.hatSchluessel ? 'ok' : 'leise', 'aria-hidden': 'true' }, m.hatSchluessel ? '✓' : '…'),
          el(
            'span',
            {},
            el('b', {}, m.ich ? `${m.name} (Sie)` : m.name),
            m.rolle === 'inhaber' ? ' · Inhaber' : '',
            ' – ',
            m.hatSchluessel ? 'hat den Schlüssel' : m.bereit ? 'wartet auf den Schlüssel' : 'hat sich noch nicht angemeldet',
          ),
        ),
      ),
    ),
    offen.length > 0 && !safe.gesperrt
      ? el(
          'div',
          { class: 'knopfreihe' },
          el('button', {
            type: 'button',
            onclick: async (e: Event) => {
              const knopf = e.target as HTMLButtonElement;
              knopf.disabled = true;
              try {
                const n = await schluesselVerteilen();
                clear(status);
                status.append(hinweis(n > 0 ? `${n} Schlüssel übergeben.` : 'Nichts zu übergeben.', 'erfolg'));
                await ladeGemeinsamenSafe(safe, inhalt);
              } catch (error) {
                clear(status);
                status.append(hinweis(fehlertext(error), 'warnung'));
                knopf.disabled = false;
              }
            },
          }, `Schlüssel jetzt geben (${offen.length})`),
        )
      : null,
    status,
  );
}

function aufnehmenZeile(safe: SafeAnzeige, inhalt: HTMLElement): HTMLElement {
  const status = el('div', {});
  const email = el('input', { type: 'email', id: `aufnehmen-email-${safe.kundeId}`, autocomplete: 'off', placeholder: 'name@beispiel.de' }) as HTMLInputElement;
  const name = el('input', { type: 'text', id: `aufnehmen-name-${safe.kundeId}`, autocomplete: 'off', placeholder: 'Vor- und Nachname' }) as HTMLInputElement;
  const knopf = el('button', { type: 'button' }, 'Aufnehmen');
  knopf.addEventListener('click', async () => {
    const adresse = email.value.trim();
    if (!adresse.includes('@')) {
      clear(status);
      status.append(hinweis('Bitte eine E-Mail-Adresse eingeben.', 'warnung'));
      return;
    }
    knopf.disabled = true;
    clear(status);
    status.append(el('p', { class: 'loading' }, 'Wird aufgenommen …'));
    try {
      const r = await mitgliedAufnehmen(safe.kundeId, adresse, name.value.trim() || undefined);
      await metadatenAuffrischen().catch(() => undefined);
      await ladeGemeinsamenSafe(safe, inhalt);
      inhalt.append(
        el(
          'div',
          { class: 'satz' },
          r.safeAngelegt
            ? `✓ ${r.name} gehört jetzt zu „${safe.titel}" und bekommt per Mail einen eigenen Safe dazu. Den Schlüssel zum gemeinsamen Safe gibt es nach der ersten Anmeldung automatisch.`
            : r.bereit
              ? `✓ ${r.name} gehört jetzt zu „${safe.titel}". Der Schlüssel wird gleich übergeben.`
              : `✓ ${r.name} gehört jetzt zu „${safe.titel}" und hat eine Einladung bekommen. Nach der ersten Anmeldung kommt der Schlüssel automatisch, sobald Sie sich wieder anmelden.`,
        ),
      );
      if (r.bereit) await schluesselVerteilen().catch(() => 0);
    } catch (error) {
      clear(status);
      status.append(hinweis(fehlertext(error), 'warnung'));
      knopf.disabled = false;
    }
  });
  return el(
    'div',
    {},
    el('h3', {}, 'Jemanden aufnehmen'),
    el('p', { class: 'leise' }, 'Die Person bekommt einen Schlüssel zu diesem Safe – wie viele Plätze es gibt, steht im Vertrag.'),
    el(
      'div',
      { class: 'einladen-zeile' },
      el('div', {}, el('label', { for: `aufnehmen-email-${safe.kundeId}` }, 'E-Mail-Adresse'), email),
      el('div', {}, el('label', { for: `aufnehmen-name-${safe.kundeId}` }, 'Name'), name),
      knopf,
    ),
    status,
  );
}

/** Fächer: oberste Ordner des Safes mit Schalter „nur für bestimmte Leute". */
async function faecherAbschnitt(safe: SafeAnzeige, mitglieder: Mitglied[], inhalt: HTMLElement): Promise<HTMLElement> {
  const kreise = await kreiseAktualisieren().catch(() => [] as GeoeffneterKreis[]);
  const oben = ordnerDesSafes(safe.kundeId).filter((o) => !o.parent_id).sort((a, b) => a.name.localeCompare(b.name, 'de'));
  const andere = mitglieder.filter((m) => !m.ich);
  return el(
    'div',
    {},
    el('h3', {}, 'Fächer nur für bestimmte Leute'),
    el('p', { class: 'leise' }, 'Ein Fach ist ein Ordner, den nicht alle sehen – nur die, die Sie ankreuzen. Sie selbst sehen jedes Fach.'),
    oben.length === 0
      ? el('p', { class: 'leise' }, 'Dieser Safe hat noch keine Ordner.')
      : el('div', {}, ...oben.map((o) => fachZeile(safe, o, kreise.find((k) => k.folderId === o.id) ?? null, andere, inhalt))),
  );
}

function fachZeile(
  safe: SafeAnzeige,
  ordner: { id: string; name: string },
  kreis: GeoeffneterKreis | null,
  andere: Mitglied[],
  inhalt: HTMLElement,
): HTMLElement {
  const status = el('div', {});
  const id = `fach-${ordner.id}`;
  const drin = new Set(kreis?.personen ?? []);
  const schalter = el('input', { type: 'checkbox', role: 'switch', class: 'schalter', id, ...(kreis ? { checked: true } : {}) }) as HTMLInputElement;

  const dokumenteIm = (): Array<{ id: string; dek: CryptoKey }> => {
    const menge = ordnerMitUnterordnern([ordner.id]);
    return state.opened.filter((d) => d.meta.folder_id && menge.has(d.meta.folder_id)).map((d) => ({ id: d.id, dek: d.dek }));
  };
  const schluesselVon = (m: Mitglied) => ({ personId: m.personId, publicKeyRaw: fromBase64(m.publicKey!), algorithm: m.algorithm! });

  schalter.addEventListener('change', async () => {
    schalter.disabled = true;
    clear(status);
    status.append(el('p', { class: 'loading' }, schalter.checked ? 'Fach wird eingerichtet …' : 'Fach wird aufgelöst …'));
    try {
      await sorgeFuerEntschluesselte();
      const org = safeSchluessel(safe.kundeId);
      if (!org) throw new Error('Dieser Safe ist nicht aufgeschlossen');
      if (schalter.checked) {
        await fachAnlegen({ kundeId: safe.kundeId, algorithm: org.algorithm }, ordner, state.me!.personId!, [], dokumenteIm());
      } else if (kreis) {
        await fachAufloesen({ kundeId: safe.kundeId, publicKeyRaw: org.publicKeyRaw, algorithm: org.algorithm }, kreis.id, dokumenteIm());
      }
      await metadatenAuffrischen().catch(() => undefined);
      await ladeGemeinsamenSafe(safe, inhalt);
      inhalt.append(
        el('div', { class: 'satz' }, schalter.checked
          ? `✓ „${ordner.name}" ist jetzt ein Fach. Kreuzen Sie an, wer es sehen darf – bis dahin nur Sie.`
          : `„${ordner.name}" sehen jetzt wieder alle in „${safe.titel}".`),
      );
    } catch (error) {
      clear(status);
      status.append(hinweis(fehlertext(error), 'warnung'));
      schalter.disabled = false;
      schalter.checked = !schalter.checked;
    }
  });

  const personen = kreis
    ? el(
        'div',
        { class: 'faecher-liste' },
        ...andere.map((m) =>
          el('label', {}, el('input', {
            type: 'checkbox',
            ...(drin.has(m.personId) ? { checked: true } : {}),
            ...(m.bereit && m.publicKey ? {} : { disabled: true }),
            onchange: async (e: Event) => {
              const box = e.target as HTMLInputElement;
              box.disabled = true;
              try {
                await fachPersonenAendern(safe.kundeId, kreis, box.checked ? [schluesselVon(m)] : [], box.checked ? [] : [m.personId]);
                clear(status);
                status.append(el('div', { class: 'satz' }, box.checked ? `✓ ${vorname(m.name)} sieht jetzt „${ordner.name}".` : `${vorname(m.name)} sieht „${ordner.name}" nicht mehr.`));
              } catch (error) {
                clear(status);
                status.append(hinweis(fehlertext(error), 'warnung'));
                box.checked = !box.checked;
              } finally {
                box.disabled = false;
              }
            },
          }), m.name, m.bereit ? '' : ' (noch nicht angemeldet)'),
        ),
      )
    : null;

  return el(
    'div',
    { class: 'fach-zeile' },
    el(
      'div',
      { class: 'fach-kopf' },
      el('label', { class: 'fach-name', for: id }, `📁 ${ordner.name}`, el('small', {}, kreis ? `Fach – ${drin.size} ${drin.size === 1 ? 'Person' : 'Personen'} außer Ihnen` : 'alle im Safe sehen diesen Ordner')),
      schalter,
    ),
    personen,
    status,
  );
}
